/**
 * Typed API client for the SENSOR Web API.
 *
 * All requests go through fetch() with credentials: 'include' so the
 * HttpOnly session cookie is sent automatically.
 *
 * Throws ApiError on any non-2xx response.
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? ''

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  })
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText)
    let message = text || res.statusText
    try {
      const parsed = JSON.parse(text) as { detail?: unknown }
      if (typeof parsed.detail === 'string') message = parsed.detail
    } catch {
      // Keep the raw response text for non-JSON errors.
    }
    throw new ApiError(res.status, message)
  }
  if (res.status === 204) {
    return undefined as T
  }
  return res.json() as Promise<T>
}

// ---------------------------------------------------------------------------
// Types mirroring backend Pydantic schemas
// ---------------------------------------------------------------------------

export interface OrganizationSchema {
  id: number
  name: string
  role: string
  created_at: string
}

export interface MeResponse {
  telegram_user_id: number
  telegram_display_name: string | null
  telegram_username: string | null
  tenant_id: number
  active_organization_id: number
  role: string
  tier: string
  can_view_dashboard: boolean
  can_export: boolean
  has_ai: boolean
  has_audience: boolean
  has_source_catalog: boolean
  is_sensor_admin: boolean
  organizations: OrganizationSchema[]
  timezone: string
}

export interface ProjectResponse {
  id: number
  organization_id: number
  name: string | null
  status: string
  is_default: boolean
  source_management_mode: 'client' | 'sensor_managed'
  user_session_id: number | null
  resolved_user_session_id: number | null
  userbot_profile_id: number | null
  userbot_label: string | null
  userbot_display_name: string | null
  created_at: string
  updated_at: string
}

export interface ProjectSummaryResponse {
  project_id: number
  name: string | null
  status: string
  total_leads: number
  hot_leads: number
  unreviewed_leads: number
  sources_count: number
}

export interface ProjectHealthResponse {
  project_id: number
  status: string
  has_active_sources: boolean
  sources_with_leads: number
  sources_total: number
  last_lead_at: string | null
  recommendation: string | null
}

export interface LeadResponse {
  id: number
  lead_project_id: number
  organization_id: number
  channel_username: string | null
  source_origin: string | null
  source_title: string | null
  sender_user_id: number | null
  sender_username: string | null
  sender_display_name: string | null
  message_id: number
  text: string
  ai_reason: string
  url: string
  base_score: number | null
  feedback_score: number | null
  decision_score: number | null
  feedback_state: string | null
  lead_status: 'new' | 'in_progress' | 'won' | 'lost'
  status_changed_at: string | null
  worked_at: string | null
  scan_id: number | null
  created_at: string
  related_messages: LeadRelatedMessage[]
}

export interface LeadRelatedMessage {
  id: number
  channel_username: string | null
  source_origin: string | null
  source_title: string | null
  message_id: number
  text: string
  ai_reason: string
  url: string
  base_score: number | null
  decision_score: number | null
  feedback_state: string | null
  lead_status: LeadResponse['lead_status']
  scan_id: number | null
  created_at: string
}

export interface OrgLeadResponse extends LeadResponse {
  /** Project name — present in org-wide inbox responses. */
  project_name: string | null
  /** Userbot label for the lead's project in org-wide inbox responses. */
  project_userbot_label: string | null
}

export interface LeadListResponse {
  items: LeadResponse[]
  total: number
  page: number
  page_size: number
}

export interface OrgLeadListResponse {
  items: OrgLeadResponse[]
  total: number
  page: number
  page_size: number
}

export interface SourceStateResponse {
  id: number
  lead_project_id: number
  organization_id: number
  chat_id: number
  topic_id: number
  group_title: string | null
  username: string | null
  source_url: string | null
  acquisition_method: string
  source_origin: string
  reader_userbot_profile_id: number | null
  reader_label: string | null
  last_access_status: string | null
  last_access_reason: string | null
  history_leads_count: number
  live_leads_count: number
  stored_leads_count: number
  liked_leads_count: number
  disliked_leads_count: number
  unsure_leads_count: number
  source_yield_score: number | null
  source_noise_score: number | null
  last_lead_at: string | null
  last_scan_at: string | null
  removed_at: string | null
  assigned_at: string
}

export interface SourceListResponse {
  items: SourceStateResponse[]
  total: number
}

export interface AudienceEvidence {
  audience_message_id: number
  quote: string
  message_link: string | null
  group_title: string | null
  message_datetime: string | null
}

export interface AudienceMember {
  id: number
  lead_project_id: number
  audience_account_id: number
  telegram_user_id: number
  display_name: string | null
  username: string | null
  bio: string | null
  is_premium: boolean
  is_verified: boolean
  is_scam: boolean
  is_fake: boolean
  messages_count: number
  last_message_at: string | null
  overall_score: number
  interest_score: number
  client_fit_score: number
  recency_score: number
  noise_score: number
  verdict: 'hot' | 'promising' | 'not_fit' | 'insufficient_data'
  portrait: string | null
  reason: string | null
  evidence: AudienceEvidence[]
  scored_at: string | null
}

export interface AudienceListResponse {
  items: AudienceMember[]
  total: number
  page: number
  page_size: number
}

export interface AudienceSummaryResponse {
  project_id: number
  total_members: number
  scored: number
  pending: number
  hot: number
  promising: number
  not_fit: number
  insufficient_data: number
}

export interface ReachTemplateResponse {
  id: number
  tag: string
  stage: 'permission' | 'offer' | string
  text: string
  is_active: boolean
  media_data: string | null
  media_mime_type: string | null
}

export interface ReachAvailabilityResponse {
  enabled: boolean
  project_id: number
  /** Number of audience candidates selected by the current audience scope. */
  candidate_count?: number
  recipient_count: number
  addressability?: ReachAddressabilitySummary | null
  templates: ReachTemplateResponse[]
}

/**
 * Addressability is deliberately not a delivery promise.  Telegram privacy
 * and flood limits can only be known when a sender account attempts a DM.
 * Keep these fields optional: older dev API responses remain readable while
 * the Reach preflight endpoint is rolled out.
 */
export interface ReachAddressabilitySummary {
  total_candidates?: number
  with_username?: number
  with_id_access_hash?: number
  without_address?: number
  dm_not_checked?: number
  unverified_dm?: number
  // Compatibility aliases used by early preflight responses.
  username?: number
  id_access_hash?: number
  no_address?: number
  not_checked?: number
  username_count?: number
  id_access_hash_count?: number
  no_address_count?: number
  dm_unchecked_count?: number
}

export interface ReachAddressabilitySummaryResponse {
  campaign_id: number
  candidate_total: number
  with_username: number
  with_access_hash: number
  no_address: number
  unverified_dm: number
}

export interface ReachCampaignResponse {
  id: number
  project_id: number
  name: string
  status: string
  /** Backward-compatible alias for recipient_count. */
  candidate_count?: number
  recipient_count: number
  addressability?: ReachAddressabilitySummary | null
  dry_run?: ReachAddressabilitySummary | null
  addressability_breakdown?: ReachAddressabilitySummary | null
  verdicts: string[]
  template_ids: number[]
  daily_limit: number
  delay_min_seconds: number
  delay_max_seconds: number
  blocked_reason: string | null
  prepared_at: string | null
  sender_accounts_required?: boolean
}

export interface ReachQueueRefreshResponse extends ReachCampaignResponse {
  candidate_total: number
  added: number
  excluded: number
  rescheduled: number
}

export interface ReachDeliveryResponse {
  id: number
  audience_account_id: number | null
  telegram_user_id: number | null
  display_name: string | null
  username: string | null
  score: number | null
  verdict: string | null
  bio?: string | null
  portrait?: string | null
  reason?: string | null
  evidence?: Array<{ audience_message_id?: number; quote?: string; [key: string]: unknown }>
  messages_count?: number | null
  last_message_at?: string | null
  interest_score?: number | null
  client_fit_score?: number | null
  recency_score?: number | null
  noise_score?: number | null
  is_premium?: boolean | null
  is_verified?: boolean | null
  is_scam?: boolean | null
  is_fake?: boolean | null
  manual_username: string | null
  manual_telegram_user_id: number | null
  rendered_message: string
  media_data: string | null
  media_mime_type: string | null
  status: string
  /** Present after addressability preflight; DM privacy is never inferred. */
  addressability_status?: 'username' | 'id_access_hash' | 'no_address' | 'manual' | string | null
  dm_status?: 'not_checked' | 'privacy_blocked' | 'sent' | string | null
  access_hash?: number | null
  has_access_hash?: boolean
  addressability?: string | null
  /** Optional historical evidence from the latest manual sandbox test. */
  message_template_id?: number | null
  template_tag?: string | null
  scheduled_at?: string | null
  sent_at?: string | null
  failure_reason?: string | null
  sandbox_run_id?: number | null
  sandbox_result_status?: string | null
  sandbox_detail?: string | null
  sandbox_sender_account_id?: number | null
  sandbox_telegram_message_id?: number | null
  sandbox_telegram_accepted?: boolean | null
  sandbox_telegram_delivery_status?: 'not_sent' | 'sent' | 'read' | 'unknown' | string | null
  sandbox_telegram_status_checked_at?: string | null
}

/** Network-free sandbox check for a manually curated test recipient set. */
export interface ReachSandboxDryRunRecipient {
  delivery_id: number
  target: string
  status: string
  error: string | null
  addressability_status?: string | null
  dm_status?: string | null
}

export interface ReachSandboxDryRunResponse {
  campaign_id: number
  mode: 'dry_run' | string
  status: string
  recipient_count: number
  recipients: ReachSandboxDryRunRecipient[]
  blocked_reason?: string | null
  sender_account_required?: boolean
}

export interface ReachSandboxSendResult {
  id: number
  reach_delivery_id: number
  /** Account selected by the server's safe rotation policy for this attempt. */
  sender_account_id?: number | null
  status: string
  detail: string | null
  flood_wait_seconds: number | null
  /** Set only after Telegram accepted the outgoing message and gave it an ID. */
  telegram_message_id?: number | null
  telegram_accepted?: boolean | null
  /** Telegram has no reliable private-chat delivery receipt: not_sent|sent|read|unknown. */
  telegram_delivery_status?: 'not_sent' | 'sent' | 'read' | 'unknown' | string | null
  /** Time when the read evidence was last checked; it is not a delivery time. */
  telegram_status_checked_at?: string | null
}

export interface ReachSandboxSendResponse {
  id: number
  campaign_id: number
  sender_account_id: number | null
  status: string
  dry_run: boolean
  explicit_confirmation: boolean
  delay_min_seconds: number
  delay_max_seconds: number
  recipient_count: number
  max_recipients: number
  error: string | null
  /** Timestamp of the latest read-evidence refresh, if this was a status check. */
  checked_at?: string | null
  results: ReachSandboxSendResult[]
}

/** Immutable local audit of prior bounded sandbox runs. No Telegram call. */
export interface ReachSandboxHistoryResult {
  id: number
  reach_delivery_id: number
  recipient_telegram_user_id: number | null
  recipient_display_name: string | null
  recipient_username: string | null
  manual_username: string | null
  manual_telegram_user_id: number | null
  sender_account_id: number | null
  status: string
  detail: string | null
  flood_wait_seconds: number | null
  telegram_message_id: number | null
  telegram_accepted: boolean
  telegram_delivery_status: 'not_sent' | 'sent' | 'read' | 'unknown' | string
  telegram_status_checked_at: string | null
  delivery_sent_at: string | null
  delivery_failure_reason: string | null
  created_at: string
}

export interface ReachSandboxHistoryRun {
  id: number
  campaign_id: number
  status: string
  dry_run: boolean
  explicit_confirmation: boolean
  delay_min_seconds: number
  delay_max_seconds: number
  recipient_count: number
  max_recipients: number
  error: string | null
  created_at: string
  completed_at: string | null
  results: ReachSandboxHistoryResult[]
}

export interface ReachManualDeliveryBatchResponse {
  /** Number of unique recipients accepted from the pasted list. */
  added: number
  /** Duplicate or invalid input tokens that were intentionally ignored. */
  skipped: string[]
  /** Deliveries created by the current batch; older dev API falls back to one-by-one. */
  deliveries: ReachDeliveryResponse[]
  recipient_count?: number
}

export interface ReachSenderAccountResponse {
  id: number
  telegram_user_id: number | null
  phone_masked: string | null
  display_name: string | null
  username: string | null
  status: string
}

export interface AdminReachSenderAssignment {
  id: number
  organization_id: number
  lead_project_id: number | null
  active: boolean
}

export interface AdminReachSenderAccount {
  id: number
  telegram_user_id: number | null
  phone_masked: string | null
  display_name: string | null
  username: string | null
  status: string
  ownership: string
  has_proxy: boolean
  daily_sent_count: number
  warmup_until: string | null
  last_used_at: string | null
  last_error: string | null
  assignments: AdminReachSenderAssignment[]
}

/**
 * Consent-first Reach workflow. A conversation is a project-scoped record of
 * the human review state for one contact; it is deliberately separate from a
 * delivery attempt. The API keeps the fields optional where an older dev
 * database has no historical enrichment yet.
 */
export type ReachConversationStatus =
  | 'queued'
  | 'permission_requested'
  | 'permission_granted'
  | 'offer_sent'
  | 'replied'
  | 'handed_to_alexey'
  | 'escalated'
  | 'needs_review'
  | 'no_response'
  | 'stopped'
  | 'opted_out'
  | 'unreachable'

export interface ReachConversationMessage {
  id: number
  direction: 'outbound' | 'inbound' | 'system' | string
  message_type: 'permission' | 'offer' | 'reply' | string
  template_tag?: string | null
  rendered_text: string
  occurred_at: string
  telegram_message_id?: number | null
}

export interface ReachConversationSummary {
  id: number
  contact_id: number
  primary_project_id: number
  project_ids: number[]
  campaign_id?: number | null
  hybrid: boolean
  audience_account_id: number | null
  contact_display_name: string | null
  contact_username: string | null
  contact_telegram_user_id?: number | null
  do_not_contact: boolean
  do_not_contact_reason?: string | null
  last_message_at?: string | null
  replied_at?: string | null
  handed_to_alexey_at?: string | null
  stopped_reason?: string | null
  latest_message?: ReachConversationMessage | null
  score?: number | null
  verdict?: string | null
  portrait?: string | null
  reason?: string | null
  source_title?: string | null
  source_message?: string | null
  status: ReachConversationStatus | string
  first_template_tag?: string | null
  second_template_tag?: string | null
}

export interface ReachConversationResponse extends ReachConversationSummary {
  bio?: string | null
  messages_count?: number | null
  evidence?: Array<{ quote?: string; message_link?: string; [key: string]: unknown }>
  messages: ReachConversationMessage[]
}

export interface ReachFunnelResponse {
  project_id: number
  total: number
  counts: Partial<Record<ReachConversationStatus, number>>
  conversations: ReachConversationSummary[]
}

export interface ReachIncomingReplyResponse {
  conversation: ReachConversationResponse
  notification_recipient_user_id: number
  notification_text: string
}

export interface AudienceJobResponse {
  id: number
  kind: 'score' | 'collect_score' | 'member_snapshot'
  status: 'requested' | 'running' | 'done' | 'failed' | 'canceled'
  progress_done: number
  progress_total: number
  result: Record<string, unknown> | null
  error: string | null
}

export interface LeadScanJobResponse {
  id: number
  status: 'requested' | 'running' | 'cancel_requested' | 'done' | 'failed' | 'canceled'
  progress_done: number
  progress_total: number
  result: Record<string, unknown> | null
  error: string | null
}

// Org-wide sources (no project filter)
export interface OrgSourceProject {
  id: number
  name: string | null
  source_management_mode?: 'client' | 'sensor_managed'
  /** Per-project source-state id, used to remove the source from this project. */
  source_state_id: number | null
}

export interface OrgSource {
  chat_id: number
  topic_id: number
  title: string | null
  username: string | null
  source_url: string | null
  acquisition_methods: string[]
  projects: OrgSourceProject[]
  total_leads: number
  history_leads_count: number
  live_leads_count: number
  liked_leads_count: number
  disliked_leads_count: number
  unsure_leads_count: number
  source_yield_score: number | null
  source_noise_score: number | null
  quality_label: 'good' | 'noisy' | 'quiet' | 'access_error' | 'new' | 'pending' | 'active'
  quality_reason: string | null
  recommendation: string | null
  last_lead_at: string | null
  last_scan_at: string | null
  access_status: string | null
  access_reason: string | null
  /** Distinct userbot labels this source is bound to (via its projects). */
  userbot_labels: string[]
}

export interface OrgSourcesResponse {
  sources: OrgSource[]
  total: number
}

// ---------------------------------------------------------------------------
// Dialog / Telegram chat types
// ---------------------------------------------------------------------------

export interface TgFolder {
  id: number
  title: string
  session_id: number | null
  userbot_label: string | null
}

export interface TgChatDialog {
  chat_id: number
  title: string
  username: string | null
  chat_type: string
  folder_ids: number[]
  monitored: boolean
  session_id: number | null
  userbot_label: string | null
  is_active_userbot: boolean
  topic_id?: number | null
  raw_source?: string | null
}

export interface DialogSnapshot {
  status: string
  folders: TgFolder[]
  chats: TgChatDialog[]
  error: string | null
}

// ---------------------------------------------------------------------------
// Chat types
// ---------------------------------------------------------------------------

export interface ChatCitation {
  url: string
  label: string
}

export interface ChatMessage {
  id: number
  role: 'user' | 'assistant'
  content: string
  citations: ChatCitation[]
  created_at: string
}

export interface IngestSource {
  title: string
  count: number
  state: 'pending' | 'active' | 'done'
}

export interface IngestProgress {
  phase: 'connecting' | 'collecting' | 'indexing'
  groups_total: number
  groups_done: number
  messages: number
  sources: IngestSource[]
}

export interface ChatSession {
  id: number
  title: string
  // Raw Conversation.status from the backend. 'active' = ready to chat;
  // 'ingesting'/'ingesting_run' = collection in progress; 'failed' = ingest error.
  status: 'ingesting' | 'ingesting_run' | 'active' | 'failed' | 'archived'
  message_count: number
  source_count: number
  group_ids: number[]
  last_activity: string
  created_at: string
  // Live ingest telemetry (present while ingesting, or after failure).
  ingest_progress?: IngestProgress | null
  ingest_error?: 'no_userbot' | 'no_messages' | 'error' | 'cancelled' | string | null
  userbot_session_id?: number | null
  userbot_label?: string | null
}

export interface ChatSessionDetail {
  id: number
  title: string
  // Raw Conversation.status from the backend. 'active' = ready to chat;
  // 'ingesting'/'ingesting_run' = collection in progress; 'failed' = ingest error.
  status: 'ingesting' | 'ingesting_run' | 'active' | 'failed' | 'archived'
  group_ids: number[]
  source_titles: string[]
  source_results: { title: string | null; count: number }[]
}

export interface ChatSessionsResponse {
  sessions: ChatSession[]
}

export interface ChatMessagesResponse {
  session: ChatSessionDetail
  messages: ChatMessage[]
}

export interface ChatSource {
  id: number
  title: string
  username: string | null
  chat_id: number
}

export interface ChatSourcesResponse {
  sources: ChatSource[]
}

export interface Userbot {
  id: number
  label: string
  telegram_user_id: number | null
  is_active: boolean
}

export interface UserbotsResponse {
  userbots: Userbot[]
}

export interface UserbotAuthStartResponse {
  request_id: number
  status: string
}

export interface UserbotAuthStatusResponse {
  request_id: number
  status: 'requested' | 'qr_pending' | 'success' | 'error' | 'expired' | 'cancelled'
  qr_image_b64: string | null
  error: string | null
  new_session_id: number | null
}

export interface ChatScope {
  mode: 'count' | 'period' | 'datetime'
  count?: number
  period_days?: number
  cutoff_iso?: string
}

export interface ExtraChat {
  chat_id?: number | null
  title: string | null
  username: string | null
  session_id?: number | null
  topic_id?: number | null
  raw_source?: string | null
}

export interface CreateChatSessionRequest {
  theme: string
  group_ids: number[]
  extra_chats: ExtraChat[]
  scope: ChatScope
}

export interface CreateScheduledTaskRequest {
  prompt: string
  group_ids: number[]
  extra_chats: ExtraChat[]
  schedule_type: 'daily' | 'weekly' | 'custom'
  schedule_time: string
  interval_days?: number | null
  max_characters?: number
}

export interface UpdateScheduledTaskRequest {
  prompt?: string
  schedule_type?: 'daily' | 'weekly' | 'custom'
  schedule_time?: string
  interval_days?: number | null
  enabled?: boolean
}

export interface ScheduledTaskResponse {
  id: number
  prompt: string
  sources: string[]
  source_details: Array<{ title: string; url: string | null }>
  schedule_type: 'daily' | 'weekly' | 'custom' | string
  schedule_time: string | null
  interval_days: number | null
  enabled: boolean
  created_at: string
  last_execution: {
    id: number
    status: string
    executed_at: string
    digest_content: string | null
    error_message: string | null
    message_count: number
  } | null
}

export interface AskChatRequest {
  question: string
}

export interface AskChatResponse {
  message: ChatMessage
}

// ---------------------------------------------------------------------------
// Source import / management types
// ---------------------------------------------------------------------------

export interface ImportFolder {
  id: number
  title: string
}

export interface ImportFoldersResponse {
  status: string
  folders: ImportFolder[]
}

export interface ImportCandidate {
  chat_id: number
  title: string | null
  username: string | null
  chat_type: string | null
  already_added: boolean
  reader_conflict: boolean
}

export interface ImportCandidatesResponse {
  status: string
  candidates: ImportCandidate[]
}

export interface ImportResult {
  added: number
  skipped_existing: number
  skipped_by_limit: number
  skipped_existing_client_reader: number
  skipped_invalid?: number
  invalid_sources?: string[]
}

export interface ManagedReader {
  profile_id: number
  active_user_session_id: number
  telegram_user_id: number | null
  label: string
  organization_id: number
  snapshot_status: string | null
  snapshot_updated_at: string | null
}

export interface ManagedReadersResponse {
  readers: ManagedReader[]
}

export interface RecheckResult {
  status: string
  ok: number
  unreadable: number
}

export interface SnapshotRefreshResult {
  status: string
}

export interface SourceRecommendation {
  id: number
  chat_id: number | null
  title: string
  username: string | null
  source_url: string | null
  kind: 'channel' | 'megagroup' | 'chat'
  member_count: number | null
  similar_to_title: string
  edge_type:
    | 'semantic_match'
    | 'tg_recommendation'
    | 'audience_overlap'
    | 'geo_match'
    | 'linked_source'
    | 'text_profile_match'
    | 'external_catalog_match'
  rank: number | null
  common_count: number | null
  overlap_ratio: number | null
  already_added: boolean
}

export interface SourceRecommendationsResponse {
  items: SourceRecommendation[]
  total: number
}

export interface SourceDiscoveryJobResponse {
  id: number
  kind: string // refresh | add
  status: string // requested | running | done | failed
  result: Record<string, unknown> | null
  error: string | null
}

export interface SourceRecommendationsRefreshResponse {
  requests: number
  stored_edges: number
  skipped_recent: number
  errors: number
  flood_wait_seconds: number | null
  quota_exceeded: boolean
  telegram_edges: number
  overlap_edges: number
  semantic_match_edges: number
  linked_source_edges: number
  geo_match_edges: number
  text_profile_edges: number
  enrichment_attempted: number
  enrichment_processed: number
  enrichment_enriched: number
  enrichment_errors: number
  enrichment_skipped: number
}

export interface AddRecommendedSourceResponse {
  assigned_total: number
  readable_total: number
  pending: number
  failed: string[]
}

// ---------------------------------------------------------------------------
// Project detail
// ---------------------------------------------------------------------------

// Structured source-of-truth for the AI intent (mirrors ai_intent_fields JSONB).
// `source`: 'fields' = authored as discrete fields, 'text' = derived from prose.
export interface AiLogicFields {
  lead: string
  context: string
  target_author: string
  avoid_role: string
  positive_examples: string[]
  negative_examples: string[]
  structured: boolean
  source: string
}

export interface ProjectDetailResponse {
  id: number
  organization_id: number
  name: string | null
  status: string
  is_default: boolean
  source_management_mode: 'client' | 'sensor_managed'
  created_at: string
  updated_at: string
  raw_input: string | null
  base_keywords: string[]
  exclude_keywords: string[]
  keywords_cap: number
  exclude_keywords_cap: number
  watch_usernames: string[]
  watch_user_ids: string[]
  ai_intent_prompt: string | null
  ai_intent_fields: AiLogicFields
  user_session_id: number | null
  resolved_user_session_id: number | null
  userbot_profile_id: number | null
  userbot_label: string | null
  userbot_display_name: string | null
}

export interface ProjectHandoffCounts {
  sources: number
  leads: number
  monitoring_matches: number
  history_scans: number
  audience_accounts: number
  audience_messages: number
  audience_scores: number
}

export interface ProjectHandoffResponse {
  source_project_id: number
  target_user_id: number
  target_username: string | null
  target_organization_id: number
  target_project_id: number | null
  already_completed: boolean
  counts: ProjectHandoffCounts
}

// ---------------------------------------------------------------------------
// Auth session management types
// ---------------------------------------------------------------------------

export interface SessionItem {
  public_id: string
  created_at: string
  last_seen_at: string
  expires_at: string
  current: boolean
  device_label: string | null
}

export interface SessionListResponse {
  sessions: SessionItem[]
}

export interface LogoutAllResponse {
  status: string
  revoked: number
}

// ---------------------------------------------------------------------------
// API methods
// ---------------------------------------------------------------------------

export const api = {
  getMe: () => request<MeResponse>('/api/v1/me'),

  getOrganizations: () => request<OrganizationSchema[]>('/api/v1/organizations'),

  updateOrganization: (id: number, name: string) =>
    request<OrganizationSchema>(`/api/v1/organizations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }),

  getProjects: () => request<ProjectResponse[]>('/api/v1/projects'),

  getProject: (id: number) => request<ProjectResponse>(`/api/v1/projects/${id}`),

  getProjectSummary: (id: number) =>
    request<ProjectSummaryResponse>(`/api/v1/projects/${id}/summary`),

  getProjectHealth: (id: number) =>
    request<ProjectHealthResponse>(`/api/v1/projects/${id}/health`),

  getAllLeads: (params?: {
    page?: number
    page_size?: number
    project_id?: number
    project_ids?: number[]
    feedback?: string[]
    hot?: boolean
    q?: string
  }) => {
    const qs = new URLSearchParams()
    if (params?.page) qs.set('page', String(params.page))
    if (params?.page_size) qs.set('page_size', String(params.page_size))
    if (params?.project_id) qs.set('project_id', String(params.project_id))
    params?.project_ids?.forEach((id) => qs.append('project_id', String(id)))
    params?.feedback?.forEach((value) => qs.append('feedback', value))
    if (params?.hot) qs.set('hot', 'true')
    if (params?.q) qs.set('q', params.q)
    const q = qs.toString()
    return request<OrgLeadListResponse>(`/api/v1/leads${q ? `?${q}` : ''}`)
  },

  getLeads: (
    projectId: number,
    params?: {
      page?: number
      page_size?: number
      hot_only?: boolean
      feedback_state?: string[]
      search?: string
      source?: string
    },
  ) => {
    const qs = new URLSearchParams()
    if (params?.page) qs.set('page', String(params.page))
    if (params?.page_size) qs.set('page_size', String(params.page_size))
    if (params?.hot_only) qs.set('hot_only', 'true')
    params?.feedback_state?.forEach((value) => qs.append('feedback_state', value))
    if (params?.search) qs.set('search', params.search)
    if (params?.source) qs.set('source', params.source)
    const q = qs.toString()
    return request<LeadListResponse>(
      `/api/v1/projects/${projectId}/leads${q ? `?${q}` : ''}`,
    )
  },

  getLead: (projectId: number, leadId: number) =>
    request<LeadResponse>(`/api/v1/projects/${projectId}/leads/${leadId}`),

  getSources: (projectId: number) =>
    request<SourceListResponse>(`/api/v1/projects/${projectId}/sources`),

  getAllSources: (projectId?: number) =>
    request<OrgSourcesResponse>(
      projectId != null ? `/api/v1/sources?project_id=${projectId}` : '/api/v1/sources',
    ),

  getSource: (projectId: number, sourceId: number) =>
    request<SourceStateResponse>(
      `/api/v1/projects/${projectId}/sources/${sourceId}`,
    ),

  getSourceRecommendations: (projectId: number, sourceStateIds?: number[]) => {
    const qs = new URLSearchParams()
    if (sourceStateIds?.length) qs.set('source_state_ids', sourceStateIds.join(','))
    const q = qs.toString()
    return request<SourceRecommendationsResponse>(
      `/api/v1/projects/${projectId}/source-recommendations${q ? `?${q}` : ''}`,
    )
  },

  refreshSourceRecommendations: (
    projectId: number,
    sourceStateIds?: number[],
    forceRefresh = false,
  ) =>
    request<SourceDiscoveryJobResponse>(
      `/api/v1/projects/${projectId}/source-recommendations/refresh`,
      {
        method: 'POST',
        body: JSON.stringify({
          source_state_ids: sourceStateIds ?? [],
          force_refresh: forceRefresh,
        }),
      },
    ),

  addRecommendedSource: (projectId: number, catalogId: number) =>
    request<SourceDiscoveryJobResponse>(
      `/api/v1/projects/${projectId}/source-recommendations/${catalogId}/add`,
      { method: 'POST' },
    ),

  getSourceDiscoveryJob: (projectId: number) =>
    request<SourceDiscoveryJobResponse | null>(
      `/api/v1/projects/${projectId}/source-recommendations/job`,
    ),

  startHistoryScan: (
    projectId: number,
    body: { days?: number; start_date?: string; source_state_ids?: number[] },
  ) =>
    request<LeadScanJobResponse>(`/api/v1/projects/${projectId}/history-scan`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  cancelHistoryScan: (projectId: number) =>
    request<LeadScanJobResponse>(`/api/v1/projects/${projectId}/history-scan/cancel`, {
      method: 'POST',
    }),

  getActiveHistoryScan: (projectId: number) =>
    request<LeadScanJobResponse | null>(
      `/api/v1/projects/${projectId}/history-scan/active`,
    ),

  getAudienceSummary: (projectId: number) =>
    request<AudienceSummaryResponse>(
      `/api/v1/audience/summary?project_id=${projectId}`,
    ),

  getReachAvailability: (projectId: number) =>
    request<ReachAvailabilityResponse>(
      `/api/v1/reach/availability?project_id=${projectId}`,
    ),

  createReachTemplate: (
    projectId: number,
    body: { tag: string; stage?: 'permission' | 'offer'; text: string; media_data?: string | null; media_mime_type?: string | null },
  ) =>
    request<ReachTemplateResponse>(`/api/v1/reach/templates?project_id=${projectId}`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  getReachCampaigns: (projectId: number) =>
    request<ReachCampaignResponse[]>(
      `/api/v1/reach/campaigns?project_id=${projectId}`,
    ),

  deleteReachCampaign: (projectId: number, campaignId: number) =>
    request<void>(`/api/v1/reach/campaigns/${campaignId}?project_id=${projectId}`, { method: 'DELETE' }),

  createReachCampaign: (
    projectId: number,
    body: { name: string; verdicts: string[]; template_ids: number[] },
  ) =>
    request<ReachCampaignResponse>(`/api/v1/reach/campaigns?project_id=${projectId}`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  preflightReachCampaign: (projectId: number, campaignId: number) =>
    request<ReachCampaignResponse>(
      `/api/v1/reach/campaigns/${campaignId}/preflight?project_id=${projectId}`,
      { method: 'POST' },
    ),

  pauseReachCampaign: (projectId: number, campaignId: number) =>
    request<ReachCampaignResponse>(
      `/api/v1/reach/campaigns/${campaignId}/pause?project_id=${projectId}`,
      { method: 'POST' },
    ),

  refreshReachCampaignQueue: (projectId: number, campaignId: number) =>
    request<ReachQueueRefreshResponse>(
      `/api/v1/reach/campaigns/${campaignId}/refresh-queue?project_id=${projectId}`,
      { method: 'POST' },
    ),

  getReachDryRun: (projectId: number, campaignId: number) =>
    request<ReachAddressabilitySummaryResponse>(
      `/api/v1/reach/campaigns/${campaignId}/dry-run?project_id=${projectId}`,
    ),

  getReachDeliveries: (projectId: number, campaignId: number) =>
    request<ReachDeliveryResponse[]>(`/api/v1/reach/campaigns/${campaignId}/deliveries?project_id=${projectId}`),

  deleteAllReachDeliveries: (projectId: number, campaignId: number) =>
    request<{ recipient_count: number }>(`/api/v1/reach/campaigns/${campaignId}/deliveries?project_id=${projectId}`, { method: 'DELETE' }),

  deleteReachDelivery: (projectId: number, campaignId: number, deliveryId: number) =>
    request<{ recipient_count: number }>(`/api/v1/reach/campaigns/${campaignId}/deliveries/${deliveryId}?project_id=${projectId}`, { method: 'DELETE' }),

  addReachManualDelivery: (projectId: number, campaignId: number, body: { username?: string; telegram_user_id?: number }) =>
    request<ReachDeliveryResponse>(`/api/v1/reach/campaigns/${campaignId}/deliveries/manual?project_id=${projectId}`, { method: 'POST', body: JSON.stringify(body) }),

  /**
   * Adds a pasted list in one request when the server supports it.  The
   * fallback keeps the dev UI usable during a rolling backend update.
   */
  addReachManualDeliveries: async (projectId: number, campaignId: number, recipients: string[]): Promise<ReachManualDeliveryBatchResponse> => {
    try {
      return await request<ReachManualDeliveryBatchResponse>(
        `/api/v1/reach/campaigns/${campaignId}/deliveries/manual/batch?project_id=${projectId}`,
        { method: 'POST', body: JSON.stringify({ recipients }) },
      )
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 404) throw error
      const deliveries = await Promise.all(recipients.map((recipient) => {
        const normalized = recipient.replace(/^@+/, '')
        return /^\d+$/.test(normalized)
          ? request<ReachDeliveryResponse>(`/api/v1/reach/campaigns/${campaignId}/deliveries/manual?project_id=${projectId}`, {
            method: 'POST', body: JSON.stringify({ telegram_user_id: Number(normalized) }),
          })
          : request<ReachDeliveryResponse>(`/api/v1/reach/campaigns/${campaignId}/deliveries/manual?project_id=${projectId}`, {
            method: 'POST', body: JSON.stringify({ username: normalized }),
          })
      }))
      return { added: deliveries.length, skipped: [], deliveries }
    }
  },

  runReachSandboxDryRun: (projectId: number, campaignId: number, body: { confirmation: boolean }) =>
    request<ReachSandboxDryRunResponse>(
      `/api/v1/reach/campaigns/${campaignId}/sandbox/dry-run?project_id=${projectId}`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  sendReachSandbox: (projectId: number, campaignId: number, body: { confirmation: boolean; delay_min_seconds?: number; delay_max_seconds?: number }) =>
    request<ReachSandboxSendResponse>(
      `/api/v1/reach/campaigns/${campaignId}/sandbox/send?project_id=${projectId}`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  checkReachSandboxStatus: (projectId: number, campaignId: number, runId: number) =>
    request<ReachSandboxSendResponse>(
      `/api/v1/reach/campaigns/${campaignId}/sandbox/${runId}/status-check?project_id=${projectId}`,
      { method: 'POST' },
    ),

  getReachSandboxHistory: (projectId: number, campaignId: number) =>
    request<ReachSandboxHistoryRun[]>(
      `/api/v1/reach/campaigns/${campaignId}/sandbox/history?project_id=${projectId}`,
    ),

  getReachSenderAccounts: (projectId: number) =>
    request<ReachSenderAccountResponse[]>(`/api/v1/reach/sender-accounts?project_id=${projectId}`),

  // ── Admin: Reach sender management ─────────────────────────────────────

  getAdminReachSenders: () =>
    request<AdminReachSenderAccount[]>(`/api/v1/admin/reach/senders`),

  setAdminReachSenderStatus: (accountId: number, status: string) =>
    request<AdminReachSenderAccount>(`/api/v1/admin/reach/senders/${accountId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  assignAdminReachSender: (accountId: number, organizationId: number, leadProjectId: number | null) =>
    request<AdminReachSenderAccount>(`/api/v1/admin/reach/senders/${accountId}/assignments`, {
      method: 'POST',
      body: JSON.stringify({ organization_id: organizationId, lead_project_id: leadProjectId }),
    }),

  detachAdminReachSender: (accountId: number, assignmentId: number) =>
    request<AdminReachSenderAccount>(`/api/v1/admin/reach/senders/${accountId}/assignments/${assignmentId}`, {
      method: 'DELETE',
    }),


  registerReachSenderFromReader: (projectId: number, userSessionId: number) =>
    request<ReachSenderAccountResponse>(`/api/v1/reach/sender-accounts/register-existing?project_id=${projectId}`, {
      method: 'POST',
      body: JSON.stringify({ user_session_id: userSessionId, confirm_reader_reuse: true }),
    }),

  getReachFunnel: (projectId: number) =>
    request<ReachFunnelResponse>(`/api/v1/reach/funnel?project_id=${projectId}`),

  getReachConversations: (projectId: number, status?: ReachConversationStatus | string) => {
    const qs = new URLSearchParams({ project_id: String(projectId) })
    if (status) qs.set('status', status)
    return request<ReachConversationSummary[]>(`/api/v1/reach/conversations?${qs.toString()}`)
  },

  getReachConversation: (projectId: number, conversationId: number) =>
    request<ReachConversationResponse>(`/api/v1/reach/conversations/${conversationId}?project_id=${projectId}`),

  createReachConversation: (
    projectId: number,
    body: { audience_account_id?: number; delivery_id?: number; related_project_ids?: number[]; hybrid?: boolean },
  ) => request<ReachConversationResponse>(`/api/v1/reach/conversations?project_id=${projectId}`, {
    method: 'POST',
    body: JSON.stringify(body),
  }),

  transitionReachConversation: (
    projectId: number,
    conversationId: number,
    body: { status: ReachConversationStatus; note?: string },
  ) => request<ReachConversationResponse>(`/api/v1/reach/conversations/${conversationId}/transition?project_id=${projectId}`, {
    method: 'POST',
    body: JSON.stringify(body),
  }),

  recordReachIncoming: (
    projectId: number,
    conversationId: number,
    body: { text: string; telegram_message_id?: number; occurred_at?: string },
  ) => request<ReachIncomingReplyResponse>(`/api/v1/reach/conversations/${conversationId}/incoming?project_id=${projectId}`, {
    method: 'POST',
    body: JSON.stringify(body),
  }),

  markReachContactDoNotContact: (projectId: number, contactId: number, reason?: string) =>
    request<ReachConversationResponse>(`/api/v1/reach/contacts/${contactId}/do-not-contact?project_id=${projectId}`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  createHybridCampaign: (sourceCampaignIds: number[], name: string, templateVariationCount: number) =>
    request<{ campaign_id: number; hybrid_recipient_count: number; moved_from_campaigns: Record<string, number> }>(`/api/v1/reach/hybrid-campaign`, {
      method: 'POST',
      body: JSON.stringify({
        source_campaign_ids: sourceCampaignIds,
        name,
        template_variation_count: templateVariationCount,
      }),
    }),

  getAudienceScores: (
    projectId: number,
    params?: {
      verdict?: AudienceMember['verdict'] | ''
      verdicts?: AudienceMember['verdict'][]
      has_username?: boolean
      page?: number
      page_size?: number
    },
  ) => {
    const qs = new URLSearchParams({ project_id: String(projectId) })
    if (params?.verdict) qs.set('verdict', params.verdict)
    if (params?.verdicts?.length) qs.set('verdicts', params.verdicts.join(','))
    if (params?.has_username) qs.set('has_username', 'true')
    if (params?.page) qs.set('page', String(params.page))
    if (params?.page_size) qs.set('page_size', String(params.page_size))
    return request<AudienceListResponse>(`/api/v1/audience/scores?${qs.toString()}`)
  },

  startAudienceScoring: (projectId: number, verdicts?: AudienceMember['verdict'][]) =>
    request<AudienceJobResponse>(
      `/api/v1/audience/score?project_id=${projectId}`,
      {
        method: 'POST',
        body: JSON.stringify({ verdicts: verdicts ?? [] }),
      },
    ),

  getActiveAudienceJob: (projectId: number) =>
    request<AudienceJobResponse | null>(
      `/api/v1/audience/jobs/active?project_id=${projectId}`,
    ),

  cancelAudienceJob: (projectId: number) =>
    request<AudienceJobResponse>(
      `/api/v1/audience/jobs/cancel?project_id=${projectId}`,
      { method: 'POST' },
    ),

  startAudienceCollect: (
    projectId: number,
    body: { mode: 'kw' | 'chat'; period_days: number; chat_ref?: string | null },
  ) =>
    request<AudienceJobResponse>(
      `/api/v1/audience/collect?project_id=${projectId}`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  startAudienceMemberSnapshot: (projectId: number) =>
    request<AudienceJobResponse>(
      `/api/v1/audience/member-snapshot?project_id=${projectId}`,
      { method: 'POST' },
    ),

  setLeadFeedback: (
    projectId: number,
    leadId: number,
    feedback: 'like' | 'dislike' | 'unsure',
  ) =>
    request<LeadResponse>(
      `/api/v1/projects/${projectId}/leads/${leadId}/feedback`,
      {
        method: 'PATCH',
        body: JSON.stringify({ feedback }),
      },
    ),

  markLeadWorked: (projectId: number, leadId: number, worked: boolean) =>
    request<LeadResponse>(
      `/api/v1/projects/${projectId}/leads/${leadId}/status`,
      {
        method: 'PATCH',
        body: JSON.stringify({ worked }),
      },
    ),

  setLeadStatus: (
    projectId: number,
    leadId: number,
    status: LeadResponse['lead_status'],
  ) =>
    request<LeadResponse>(
      `/api/v1/projects/${projectId}/leads/${leadId}/status`,
      {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      },
    ),

  exportLeads: async (projectId: number): Promise<Blob> => {
    const res = await fetch(`${BASE_URL}/api/v1/projects/${projectId}/leads/export`, {
      method: 'POST',
      credentials: 'include',
    })
    if (!res.ok) {
      const text = await res.text().catch(() => res.statusText)
      throw new ApiError(res.status, text)
    }
    return res.blob()
  },

  exportAudience: async (projectId: number, params?: { has_username?: boolean }): Promise<Blob> => {
    const qs = new URLSearchParams({ project_id: String(projectId) })
    if (params?.has_username) qs.set('has_username', 'true')
    const res = await fetch(`${BASE_URL}/api/v1/audience/export?${qs.toString()}`, {
      method: 'POST',
      credentials: 'include',
    })
    if (!res.ok) {
      const text = await res.text().catch(() => res.statusText)
      throw new ApiError(res.status, text)
    }
    return res.blob()
  },

  consumeLoginToken: (token: string) =>
    request<void>('/api/v1/auth/bot-token/consume', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    }),

  logout: () =>
    request<void>('/api/v1/auth/logout', { method: 'POST' }),

  getSessions: () =>
    request<SessionListResponse>('/api/v1/auth/sessions'),

  logoutAll: () =>
    request<LogoutAllResponse>('/api/v1/auth/logout-all', { method: 'POST' }),

  revokeSession: (publicId: string) =>
    request<{ status: string }>(`/api/v1/auth/sessions/${encodeURIComponent(publicId)}/revoke`, { method: 'POST' }),

  // Chat
  getChatSessions: () =>
    request<ChatSessionsResponse>('/api/v1/chat/sessions'),

  getChatMessages: (sessionId: number) =>
    request<ChatMessagesResponse>(`/api/v1/chat/sessions/${sessionId}/messages`),

  askChat: (sessionId: number, question: string) =>
    request<AskChatResponse>(`/api/v1/chat/sessions/${sessionId}/ask`, {
      method: 'POST',
      body: JSON.stringify({ question } satisfies AskChatRequest),
    }),

  refreshChatSession: (sessionId: number) =>
    request<ChatSession>(`/api/v1/chat/sessions/${sessionId}/refresh`, {
      method: 'POST',
    }),

  cancelChatSession: (sessionId: number) =>
    request<ChatSession>(`/api/v1/chat/sessions/${sessionId}/cancel`, {
      method: 'POST',
    }),

  getChatSources: () =>
    request<ChatSourcesResponse>('/api/v1/chat/sources'),

  createChatSession: (data: CreateChatSessionRequest) =>
    request<{ session: ChatSession }>('/api/v1/chat/sessions', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  createScheduledTask: (data: CreateScheduledTaskRequest) =>
    request<{ task: ScheduledTaskResponse }>('/api/v1/chat/tasks', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateScheduledTask: (taskId: number, data: UpdateScheduledTaskRequest) =>
    request<{ task: ScheduledTaskResponse }>(`/api/v1/chat/tasks/${taskId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  getScheduledTasks: () =>
    request<{ tasks: ScheduledTaskResponse[] }>('/api/v1/chat/tasks'),

  deleteChatSession: (sessionId: number) =>
    request<void>(`/api/v1/chat/sessions/${sessionId}`, { method: 'DELETE' }),

  deleteAllChatSessions: () =>
    request<{ deleted: number }>('/api/v1/chat/sessions', { method: 'DELETE' }),

  getDialogs: (activeOnly = false) =>
    request<DialogSnapshot>(`/api/v1/chat/dialogs?active_only=${activeOnly ? 'true' : 'false'}`),

  refreshDialogs: (activeOnly = false) =>
    request<{ status: string; error?: string | null }>(`/api/v1/chat/dialogs/refresh?active_only=${activeOnly ? 'true' : 'false'}`, { method: 'POST' }),

  exportDialogs: async (activeOnly = false): Promise<Blob> => {
    const res = await fetch(`${BASE_URL}/api/v1/chat/dialogs/export?active_only=${activeOnly ? 'true' : 'false'}`, {
      method: 'GET',
      credentials: 'include',
    })
    if (!res.ok) {
      const text = await res.text().catch(() => res.statusText)
      throw new ApiError(res.status, text)
    }
    return res.blob()
  },

  updateTimezone: (timezone: string) =>
    request<{ timezone: string }>('/api/v1/me/timezone', {
      method: 'PATCH',
      body: JSON.stringify({ timezone }),
    }),

  getUserbots: () =>
    request<UserbotsResponse>('/api/v1/me/userbots'),

  selectUserbot: (id: number) =>
    request<{ active_session_id: number }>(`/api/v1/me/userbots/${id}/select`, {
      method: 'POST',
    }),

  // Connect a new userbot via QR (cross-process flow; poll the status endpoint).
  startUserbotQrAuth: () =>
    request<UserbotAuthStartResponse>('/api/v1/me/userbots/connect/qr/start', {
      method: 'POST',
    }),

  getUserbotQrStatus: (requestId: number) =>
    request<UserbotAuthStatusResponse>(`/api/v1/me/userbots/connect/qr/${requestId}`),

  cancelUserbotQrAuth: (requestId: number) =>
    request<{ status: string }>(`/api/v1/me/userbots/connect/qr/${requestId}/cancel`, {
      method: 'POST',
    }),

  // Project detail & management
  draftProjectFromDescription: (description: string) =>
    request<ProjectDetailResponse>('/api/v1/projects/draft-from-description', {
      method: 'POST',
      body: JSON.stringify({ description }),
    }),

  getProjectDetail: (id: number) =>
    request<ProjectDetailResponse>(`/api/v1/projects/${id}/detail`),

  updateProject: (id: number, body: { name?: string | null; raw_input?: string | null }) =>
    request<ProjectDetailResponse>(`/api/v1/projects/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  setProjectStatus: (id: number, status: 'active' | 'archived' | 'draft') =>
    request<ProjectDetailResponse>(`/api/v1/projects/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  deleteProject: async (id: number): Promise<void> => {
    const res = await fetch(`${BASE_URL}/api/v1/projects/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    })
    if (!res.ok) {
      const text = await res.text().catch(() => res.statusText)
      throw new ApiError(res.status, text)
    }
  },

  bindProjectUserbot: (id: number, user_session_id: number) =>
    request<ProjectDetailResponse>(`/api/v1/projects/${id}/bind-userbot`, {
      method: 'POST',
      body: JSON.stringify({ user_session_id }),
    }),

  activateProject: (id: number) =>
    request<ProjectDetailResponse>(`/api/v1/projects/${id}/activate`, {
      method: 'POST',
    }),

  duplicateProject: (id: number, newName?: string) =>
    request<ProjectDetailResponse>(`/api/v1/projects/${id}/duplicate`, {
      method: 'POST',
      body: JSON.stringify({ new_name: newName ?? null }),
    }),

  updateProjectKeywords: (id: number, keywords: string[]) =>
    request<ProjectDetailResponse>(`/api/v1/projects/${id}/keywords`, {
      method: 'PATCH',
      body: JSON.stringify({ keywords }),
    }),

  updateProjectExcludeKeywords: (id: number, exclude_keywords: string[]) =>
    request<ProjectDetailResponse>(`/api/v1/projects/${id}/exclude-keywords`, {
      method: 'PATCH',
      body: JSON.stringify({ exclude_keywords }),
    }),

  updateProjectAiPrompt: (
    id: number,
    ai_intent_prompt: string,
    fields?: AiLogicFields,
  ) =>
    request<ProjectDetailResponse>(`/api/v1/projects/${id}/ai-prompt`, {
      method: 'PATCH',
      body: JSON.stringify(fields ? { ai_intent_prompt, fields } : { ai_intent_prompt }),
    }),

  updateProjectWatchUsers: (id: number, usernames: string[]) =>
    request<ProjectDetailResponse>(`/api/v1/projects/${id}/watch-users`, {
      method: 'PATCH',
      body: JSON.stringify({ usernames }),
    }),

  previewProjectHandoff: (id: number, target: string) =>
    request<ProjectHandoffResponse>(`/api/v1/admin/project-handoffs/${id}/preview`, {
      method: 'POST',
      body: JSON.stringify({ target }),
    }),

  executeProjectHandoff: (id: number, target: string) =>
    request<ProjectHandoffResponse>(`/api/v1/admin/project-handoffs/${id}`, {
      method: 'POST',
      body: JSON.stringify({ target, confirm: true }),
    }),

  // Source import / management
  getImportFolders: (projectId: number) =>
    request<ImportFoldersResponse>(
      `/api/v1/projects/${projectId}/source-import/folders`,
    ),

  refreshImport: (projectId: number) =>
    request<SnapshotRefreshResult>(
      `/api/v1/projects/${projectId}/source-import/refresh`,
      { method: 'POST' },
    ),

  getImportCandidates: (projectId: number, folderId?: number) => {
    const qs = folderId !== undefined ? `?folder_id=${folderId}` : ''
    return request<ImportCandidatesResponse>(
      `/api/v1/projects/${projectId}/source-import/candidates${qs}`,
    )
  },

  applyImport: (projectId: number, chatIds: number[]) =>
    request<ImportResult>(`/api/v1/projects/${projectId}/source-import/apply`, {
      method: 'POST',
      body: JSON.stringify({ chat_ids: chatIds }),
    }),

  getManagedReaders: (telegramUserId = 6477828562) =>
    request<ManagedReadersResponse>(
      `/api/v1/admin/managed-sources/readers?telegram_user_id=${telegramUserId}`,
    ),

  grantManagedReader: (projectId: number, profileId: number) =>
    request<ManagedReader>(
      `/api/v1/admin/managed-sources/projects/${projectId}/readers/${profileId}/grant`,
      { method: 'POST' },
    ),

  refreshManagedReaderSnapshot: (projectId: number, profileId: number) =>
    request<SnapshotRefreshResult>(
      `/api/v1/admin/managed-sources/projects/${projectId}/readers/${profileId}/snapshot/refresh`,
      { method: 'POST' },
    ),

  getManagedReaderFolders: (projectId: number, profileId: number) =>
    request<ImportFoldersResponse>(
      `/api/v1/admin/managed-sources/projects/${projectId}/readers/${profileId}/folders`,
    ),

  getManagedReaderCandidates: (
    projectId: number,
    profileId: number,
    folderId?: number,
  ) => {
    const qs = folderId !== undefined ? `?folder_id=${folderId}` : ''
    return request<ImportCandidatesResponse>(
      `/api/v1/admin/managed-sources/projects/${projectId}/readers/${profileId}/candidates${qs}`,
    )
  },

  applyManagedReaderImport: (projectId: number, profileId: number, chatIds: number[]) =>
    request<ImportResult>(
      `/api/v1/admin/managed-sources/projects/${projectId}/readers/${profileId}/import`,
      {
        method: 'POST',
        body: JSON.stringify({ chat_ids: chatIds }),
      },
    ),

  addManualSource: (projectId: number, input: string) =>
    request<ImportResult>(`/api/v1/projects/${projectId}/sources/manual`, {
      method: 'POST',
      body: JSON.stringify({ input }),
    }),

  removeSource: async (projectId: number, sourceStateId: number): Promise<void> => {
    const res = await fetch(
      `${BASE_URL}/api/v1/projects/${projectId}/sources/${sourceStateId}`,
      { method: 'DELETE', credentials: 'include' },
    )
    if (!res.ok) {
      const text = await res.text().catch(() => res.statusText)
      throw new ApiError(res.status, text)
    }
  },

  recheckSources: (projectId: number) =>
    request<RecheckResult>(
      `/api/v1/projects/${projectId}/sources/recheck-access`,
      { method: 'POST' },
    ),
}
